import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { unformatCnpj } from '../utils/cnpj.util';

import { ApiService } from './api.service';

export interface RegisterRequest {
  firstName: string;
  lastName: string;
  email: string;
  displayName: string;
  login: string;
  password: string;
  confirmPassword: string;
  document: string;
  documentType: string;
  phone: string;
  business: string;
  role: string;
  addresses: Address[];
  confirmationUrl: string;
}

export interface Address {
  address: string;
  neighborhood: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  complement: string;
  main: boolean;
  type: string;
  number: string;
}

export interface RequiredField {
  name: string;
  label: string;
  type: string;
  description: string;
  value?: string;
  defaultValue?: string;
}

export interface ProductData {
  requiredFields: RequiredField[];
  selfConfirmedOwnership?: boolean;
  confirmedNoOtherGoogleAccount?: boolean;
  confirmedDomainOwnership?: boolean;
}

export interface TokenUpdateRequest {
  email: string;
  product: ProductData;
  originActivation?: 'HUB';
}

export interface RegisterResponse {
  status: string;
  msg: string;
  data: {
    id: number;
    uuid: string;
    email: string;
    userRequest: RegisterRequest;
    token: string;
    createdOdin: boolean;
    createdUCloud: boolean;
    isCompany: boolean;
    document: string;
    createdAt: string;
    expiresAt: string;
  };
  error: boolean;
  errors: string[];
  page: number;
  nextPage: number;
  lastPage: number;
  nextPageUrl: string;
}

@Injectable({
  providedIn: 'root',
})
export class RegisterService {
  private readonly API_URL = '/user';
  private apiService = inject(ApiService);


  register(userData: RegisterRequest): Observable<RegisterResponse> {
    return this.apiService.post<RegisterResponse>(
      `${this.API_URL}/registration`,
      userData
    );
  }

  resendConfirmationEmail(email: string): Observable<any> {
    return this.apiService.get(`${this.API_URL}/registration/email/${email}`);
  }

  generateUUID(): string {
    return crypto.randomUUID();
  }

  formatPhone(phone: string): string {
    // Remove tudo que não é dígito
    const numbers = phone.replace(/\D/g, '');

    // Aplica a máscara (XX) XXXXX-XXXX
    if (numbers.length === 11) {
      return numbers.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
    } else if (numbers.length === 10) {
      return numbers.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
    }
    return phone;
  }

  formatCEP(cep: string): string {
    // Remove tudo que não é dígito
    const numbers = cep.replace(/\D/g, '');

    // Aplica a máscara XXXXX-XXX
    return numbers.replace(/^(\d{5})(\d{3})$/, '$1-$2');
  }

  cleanDocument(document: string): string {
    return unformatCnpj(document);
  }

  cleanPhone(phone: string): string {
    return phone.replace(/\D/g, '');
  }

  cleanCEP(cep: string): string {
    return cep.replace(/\D/g, '');
  }

  checkDocumentExists(
    documentType: string,
    document: string
  ): Observable<boolean> {
    const cleanDoc = this.cleanDocument(document);

    return this.apiService
      .get<boolean>(`${this.API_URL}/${documentType}/${encodeURIComponent(cleanDoc)}`)
      .pipe(
        map(response => {
          return response;
        })
      );
  }
  
}
